import { LoadingButton } from "@mui/lab";
import {
	Box,
	Button,
	Dialog,
	DialogActions,
	DialogContent,
	DialogTitle,
	FormControlLabel,
	FormGroup,
	Switch,
	TextField,
} from "@mui/material";
import { Contact, ContactType } from "@repo/server-api";
import {
	AddressInformation,
	AddressType,
	BankAccountType,
	BillingInformationType,
	TabKeys,
} from "@repo/server-api/types";
import { useTranslation } from "next-i18next";
import React, { useState, useEffect, useRef, JSX, forwardRef, useImperativeHandle } from "react";
import * as Yup from "yup";

import { validErrorState } from "@repo/legacy/components/helper/Index";
import Address from "@repo/legacy/components/shared/additionalInformation/Address";
import BankAccount from "@repo/legacy/components/shared/additionalInformation/BankAccount";

import Tabs from "../Tabs";

export type DialogBillingInformationRef = {
	show: (billingInfo: BillingInformationType) => void;
};

const DialogBillingInformation = forwardRef<
	DialogBillingInformationRef,
	Readonly<{
		onSave: (info: BillingInformationType) => void;
		contact: Contact | null;
	}>
>((props, ref) => {
	const { t } = useTranslation();

	const [open, setOpen] = useState<boolean>(false);
	const [tabs, setTabs] = useState<{ label: string; key: TabKeys; content: JSX.Element }[]>([]);
	const [tabIndex, setTabIndex] = useState<number>(0);
	const [isLoaded, setIsLoaded] = useState<boolean>(false);
	const [info, setInfo] = useState<BillingInformationType>({
		key: Math.random() * 1000,
		id: "",
	} as BillingInformationType);
	const [saveClicked, setSaveClicked] = useState<boolean>(false);

	const [errors, setErrors] = useState<Record<string, boolean | object | string>>({});
	const billingAddressRef = useRef<{
		validate: () => Promise<Record<string, boolean | object | string>>;
	} | null>(null);
	const shippingAddressRef = useRef<{
		validate: () => Promise<Record<string, boolean | object | string>>;
	} | null>(null);
	const bankAccountsRef = useRef<{
		validate: () => Promise<Record<string, boolean | object | string>>;
	} | null>(null);

	useImperativeHandle(ref, () => ({
		show: (billingInfo: BillingInformationType) => {
			setInfo({
				...billingInfo,
				id: billingInfo.id === "new" ? "" : billingInfo.id,
			});
			setIsLoaded(true);
			setOpen(true);
			setTabIndex(0);
			setErrors({});
			setSaveClicked(false);
		},
	}));

	const allTabs = [
		{
			label: t("billing-information.billing-address"),
			key: TabKeys.BILLING_ADDRESS,
			content: (
				<Address
					isLoaded={isLoaded}
					ref={billingAddressRef}
					contact={props.contact}
					setErrors={(value: Record<string, string | object | boolean>) =>
						setErrors({
							...errors,
							billingAddress: {
								...(errors.billingAddress as Record<string, string>),
								...value,
							},
						})
					}
					errors={errors.billingAddress as Record<string, string>}
					address={{
						...info.billingAddress,
						id: "",
						__typename: "AddressInformation",
					}}
					setAddress={(value: AddressInformation) =>
						setInfo({
							...info,
							billingAddress: {
								...info.billingAddress,
								...value,
								__typename: "Address",
							},
						})
					}
				/>
			),
		},
		{
			label: t("billing-information.shipping-address"),
			key: TabKeys.SHIPPING_ADDRESS,
			content: (
				<Address
					isLoaded={isLoaded}
					ref={shippingAddressRef}
					contact={props.contact}
					setErrors={(value: Record<string, string | object | boolean>) =>
						setErrors({
							...errors,
							shippingAddress: {
								...(errors.shippingAddress as Record<string, string>),
								...value,
							},
						})
					}
					errors={errors.shippingAddress as Record<string, string>}
					address={{
						...info.shippingAddress,
						id: "",
						__typename: "AddressInformation",
					}}
					setAddress={(value: AddressInformation) =>
						setInfo({
							...info,
							shippingAddress: {
								...info.shippingAddress,
								...value,
								__typename: "Address",
							},
						})
					}
				/>
			),
		},
		{
			label: t("bank-account"),
			key: TabKeys.BANK_ACCOUNT,
			content: (
				<BankAccount
					isLoaded={isLoaded}
					ref={bankAccountsRef}
					setErrors={(value) =>
						setErrors({
							...errors,
							bankAccount: {
								...(errors.bankAccount as Record<string, string>),
								...value,
							},
						})
					}
					errors={errors.bankAccount as Record<string, string>}
					bankAccount={{
						...info.bankAccount,
					}}
					setBankAccount={(value: BankAccountType) =>
						setInfo({
							...info,
							bankAccount: {
								...info.bankAccount,
								...value,
							},
						})
					}
				/>
			),
		},
	];

	const refreshTabs = () => {
		if (info.isSameAsShippingAddress) {
			// Hide shipping address tab when "same as billing" is checked
			setTabs(allTabs.filter((tab) => tab.key !== TabKeys.SHIPPING_ADDRESS));
		} else {
			setTabs(allTabs);
		}
	};

	useEffect(() => {
		setTabIndex(0);
	}, [info.isSameAsShippingAddress]);

	useEffect(() => {
		if (isLoaded) refreshTabs();
	}, [errors, info, isLoaded, info.bankAccount, info.billingAddress, info.shippingAddress]);

	const validationSchema = Yup.object().shape({
		name: Yup.string().required(t("validation.name.required")),
	});

	const validateFields = async () => {
		const localErrors: Record<string, boolean | object | string> = {};
		try {
			await validationSchema.validate(info, { abortEarly: false });
		} catch (err) {
			if (err instanceof Yup.ValidationError) {
				err.inner.forEach((error) => {
					if (error.path) {
						localErrors[error.path] = error.message;
					}
				});
			}
		}

		localErrors.billingAddress = (await billingAddressRef.current?.validate()) || {};
		// Skip shipping address validation if it's same as billing (not rendered)
		if (!info.isSameAsShippingAddress) {
			localErrors.shippingAddress = (await shippingAddressRef.current?.validate()) || {};
		}
		if (!props.contact?.contactTypes?.includes(ContactType.Client))
			localErrors.bankAccount = (await bankAccountsRef.current?.validate()) || {};

		setErrors({ ...localErrors });

		return validErrorState(localErrors);
	};

	const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();

		const isValid = await validateFields();

		if (isValid) {
			setSaveClicked(true);
			// If "same as billing" is checked, copy billing address to shipping address
			const dataToSave = info.isSameAsShippingAddress
				? { ...info, shippingAddress: { ...info.billingAddress } }
				: info;
			props.onSave(dataToSave);
			handleClose();
		}
	};

	const resetInfo = () => {
		setInfo({
			key: Math.random() * 1000,
			id: "",
			__typename: "BillingInformation",
			name: "",
			isSameAsShippingAddress: false,
			billingAddress: {
				type: AddressType.German,
				street: "",
				state: "",
				city: "",
				zipCode: "",
				country: "",
				name: "",
			},
			shippingAddress: {
				type: AddressType.German,
				street: "",
				state: "",
				city: "",
				zipCode: "",
				country: "",
				name: "",
			},
			bankAccount: {
				bankName: "",
				iban: "",
				bic: "",
				clientReference: "",
				clientReferenceDate: null,
			},
		});
	};

	const handleClose = () => {
		setOpen(false);
		setIsLoaded(false);
		setErrors({});
		resetInfo();
		setSaveClicked(false);
	};

	return (
		<Dialog
			open={open}
			PaperProps={{
				noValidate: true,
				autoComplete: "off",
				component: "form",
				onSubmit: handleSubmit,
				"data-testid": "billing-information-dialog",
			}}
			maxWidth="md"
			fullWidth={true}>
			<DialogTitle data-testid="billing-information-title">{t("billing-information")}</DialogTitle>
			<DialogContent className="flex-col gap-5">
				<Box className="flex-between">
					<TextField
						value={info.name}
						onChange={(event) => {
							setInfo({ ...info, name: event.target.value });
							setErrors({ ...errors, name: "" });
						}}
						error={!!errors.name}
						helperText={errors.name as string}
						label={t("name")}
						variant="outlined"
						className="w-45"
						name="name"
						data-testid="billing-info-name-input"
					/>
					<FormGroup className="padding7">
						<FormControlLabel
							control={
								<Switch
									onChange={(event) => {
										setInfo({
											...info,
											isSameAsShippingAddress: event.target.checked,
										});
										setErrors({});
									}}
									checked={info.isSameAsShippingAddress}
								/>
							}
							label={t("shipping-same-as-billing")}
						/>
					</FormGroup>
				</Box>
				<div className="w-100">
					<Tabs
						errors={errors as Record<string, string>}
						tabs={tabs}
						tabIndex={tabIndex}
						setTabIndex={setTabIndex}
					/>
				</div>
			</DialogContent>
			<DialogActions>
				<Button data-testid="billing-info-cancel-button" variant="outlined" onClick={handleClose}>
					{t("cancel")}
				</Button>
				<LoadingButton
					loading={saveClicked}
					type="submit"
					variant="contained"
					data-testid="billing-info-save-button"
					className={saveClicked ? "" : "blueButton"}>
					{t("save")}
				</LoadingButton>
			</DialogActions>
		</Dialog>
	);
});

DialogBillingInformation.displayName = "DialogBillingInformation";
export default DialogBillingInformation;
